import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "./firebase";
import {
  doc,
  getDoc,
  collection,
  onSnapshot,
  query,
  where,
  updateDoc,
} from "firebase/firestore";
import TutorLayout from "./TutorLayout";
import { BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import calculatorImg from "./assets/calculator.png";
import noCalculatorImg from "./assets/no-calculator.png";

const TutorAssignmentViewer = () => {
  const isLatex = (text) => {
  if (!text) return false;
  return /\\[a-zA-Z]+|\\frac|\\sqrt|\\pounds/.test(text);
};

  const { assignmentId, studentUID } = useParams();
  const [assignment, setAssignment] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [responses, setResponses] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);

  useEffect(() => {
    const unsubResponses = onSnapshot(
      query(
        collection(db, "responses"),
        where("assignmentId", "==", assignmentId),
        where("studentUID", "==", studentUID)
      ),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => doc.data());
        setResponses(data);
      }
    );

    const fetchAssignment = async () => {
      const assignmentRef = doc(db, "assignments", assignmentId);
      const assignmentSnap = await getDoc(assignmentRef);
      const assignmentData = assignmentSnap.data();
      setAssignment(assignmentData);

      const questionDocs = await Promise.all(
        assignmentData.questions.map((qid) => getDoc(doc(db, "questions", qid)))
      );

      const fetchedQuestions = questionDocs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setQuestions(fetchedQuestions);
    };

    fetchAssignment();
    return () => unsubResponses();
  }, [assignmentId, studentUID]);

  const renderProgress = () => (
    <div style={styles.progressWrapper}>
      {questions.map((_, index) => {
        const res = responses.find((r) => r.questionIndex === index);
        let bgColor = "#636e72";
        if (res?.isCorrect) bgColor = "#00b894";
        else if (res) bgColor = "#d63031";

        return (
          <div
            key={index}
            onClick={() => setCurrent(index)}
            style={{
              ...styles.progressDot,
              backgroundColor: bgColor,
              border: index === current ? "3px solid #0984e3" : "2px solid transparent",
            }}
          >
            <span style={styles.dotNumber}>{index + 1}</span>
          </div>
        );
      })}
    </div>
  );

  if (!assignment || questions.length === 0) {
    return (
      <TutorLayout>
        <div style={styles.container}>Loading assignment...</div>
      </TutorLayout>
    );
  }

  const question = questions[current];
  const response = responses.find((r) => r.questionIndex === current);
  const correctAnswers = question.answers?.length ? question.answers : [question.answer || ""];

  const maxAttempts = 3;
  const attemptsMade = response ? response.attempts || 1 : 0;
  const attemptsLeft = maxAttempts - attemptsMade;

  const handleCheckAnswer = async () => {
    if (attemptsLeft <= 0) return;

    const isCorrect = response?.answer === correctAnswers[0];
    const updatedResponse = {
      ...response,
      attempts: attemptsMade + 1,
      isCorrect,
      answer: response?.answer || "",
    };

    const responseRef = doc(
      db,
      "responses",
      `${assignmentId}_${studentUID}_${current}`
    );
    await updateDoc(responseRef, updatedResponse);

    const updatedResponses = responses.map((r) =>
      r.questionIndex === current ? updatedResponse : r
    );
    setResponses(updatedResponses);
  };

  return (
    <TutorLayout>
      <div style={styles.container}>
        <h1 style={styles.heading}>📊 Live Progress: {assignment.title}</h1>
        {renderProgress()}

        <div style={styles.card}>
        <div style={styles.questionHeader}>
  <h3 style={{ margin: 0 }}>
    Question {current + 1} of {questions.length}
  </h3>
  <span style={styles.attemptBadge}>
    {attemptsLeft} attempt{attemptsLeft !== 1 ? 's' : ''} left
  </span>
</div>


          <img
            src={question.calculator === "yes" ? calculatorImg : noCalculatorImg}
            alt={question.calculator === "yes" ? "Calculator Allowed" : "No Calculator"}
            style={{ height: 50, marginBottom: 10 }}
          />

{question.questionParagraphs && question.questionParagraphs.length > 0 ? (
  question.questionParagraphs.map((p, i) => {
    if (typeof p === "string") {
      if (p.startsWith("http") && (p.endsWith(".png") || p.endsWith(".jpg") || p.endsWith(".jpeg") || p.endsWith(".webp"))) {
        return (
          <img
            key={i}
            src={p}
            alt={`Question Image ${i + 1}`}
            style={{ maxWidth: "100%", maxHeight: "300px" }}
          />
        );
      }
      return isLatex(p) ? (
        <BlockMath key={i} math={p} />
      ) : (
        <p key={i}>{p}</p>
      );
    } else if (typeof p === "object" && p !== null) {
      return (
        <div key={i} style={{ marginBottom: "1rem" }}>
          {p.text && (isLatex(p.text) ? <BlockMath math={p.text} /> : <p>{p.text}</p>)}
          {p.image && (
            <img
              src={p.image}
              alt={`Question Image ${i + 1}`}
              style={{ maxWidth: "100%", maxHeight: "300px" }}
            />
          )}
        </div>
      );
    } else {
      return <p key={i}>{String(p)}</p>;
    }
  })
) : (
  <>
    {question.questionText && (
      isLatex(question.questionText)
        ? <BlockMath math={question.questionText} />
        : <p><strong>{question.questionText}</strong></p>
    )}
    {question.questionLatex && <BlockMath math={String(question.questionLatex)} />}
  </>
)}


          {question.optionalQuestionText && <p>{question.optionalQuestionText}</p>}

          <div style={{ marginTop: "1.5rem" }}>
            <p><strong>🧠 Student's Working:</strong></p>
            <p>
  {response?.working
    ? (isLatex(response.working)
        ? <BlockMath math={response.working} />
        : <span>{response.working}</span>)
    : "—"}
</p>

          </div>

          <div style={{ marginTop: "1rem" }}>
  <p><strong>✏️ Student's Answer:</strong></p>
  {response?.answer ? (
    <div
      style={{
        ...styles.answerBox,
        ...(response.isCorrect
          ? styles.answerCorrect
          : styles.answerIncorrect),
      }}
    >
      {isLatex(response.answer)
  ? <BlockMath math={response.answer} />
  : <span>{response.answer}</span>}

    </div>
  ) : (
    <p>—</p>
  )}
</div>


          <div style={{ marginTop: "1rem" }}>
            {response ? (
              <p>{response?.isCorrect ? "✅ Correct!" : "❌ Incorrect"}</p>
            ) : (
              <p>—</p>
            )}
          </div>

         

          <div style={{ marginTop: "1rem" }}>
 
  {!showAnswer ? (
    <button
      onClick={() => setShowAnswer(true)}
      style={{
        padding: "0.5rem 1rem",
        backgroundColor: "#636e72",
        color: "#fff",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontWeight: "bold"
      }}
    >
      Reveal Answer
    </button>
  ) : (
    <>
      <div style={{ color: "#e74c3c", fontSize: "1.4rem" }}>
        {isLatex(correctAnswers[0])
  ? <BlockMath math={correctAnswers[0]} />
  : <span>{correctAnswers[0]}</span>}

      </div>
      <button
        onClick={() => setShowAnswer(false)}
        style={{
          marginTop: "0.5rem",
          padding: "0.4rem 0.9rem",
          backgroundColor: "#dfe6e9",
          color: "#2d3436",
          border: "1px solid #b2bec3",
          borderRadius: "6px",
          cursor: "pointer",
          fontWeight: "bold"
        }}
      >
        Hide Answer
      </button>
    </>
  )}
</div>


          <div style={{ marginTop: "1rem" }}>
            <button onClick={handleCheckAnswer} style={styles.submitBtn}>
              Check Answer
            </button>
          </div>
        </div>
      </div>
    </TutorLayout>
  );
};

const styles = {
  container: {
    padding: "2rem",
    fontFamily: "'Fredoka', cursive",
    backgroundColor: "#eaf4fc",
    minHeight: "100vh",
  },
  heading: {
    fontSize: "2rem",
    marginBottom: "1rem",
  },
  card: {
    backgroundColor: "#fff",
    padding: "2rem",
    borderRadius: "12px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    maxWidth: "700px",
    margin: "1rem auto 0",
  },
  progressWrapper: {
    display: "flex",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "1.5rem",
    flexWrap: "wrap",
  },
  progressDot: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "bold",
    fontSize: "1rem",
    color: "white",
    transition: "all 0.3s ease",
    cursor: "pointer",
  },
  dotNumber: {
    userSelect: "none",
  },
  submitBtn: {
    padding: "0.6rem 1.2rem",
    backgroundColor: "#0984e3",
    color: "white",
    fontWeight: "bold",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  questionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem"
  },
  attemptBadge: {
    backgroundColor: "#f9ca24", // golden yellow
    padding: "6px 12px",
    borderRadius: "20px",
    fontWeight: "bold",
    fontSize: "0.9rem",
    color: "#2d3436"
  }, 
  answerBox: {
    padding: "0.75rem 1rem",
    borderRadius: "8px",
    marginTop: "0.5rem",
    width: "100%", // Makes it full width of the parent
    fontWeight: "bold",
    fontSize: "1.4rem",
    boxSizing: "border-box",
  },
  answerCorrect: {
    backgroundColor: "#d4edda",
    border: "2px solid #28a745",
    color: "#155724"
  },
  answerIncorrect: {
    backgroundColor: "#f8d7da",
    border: "2px solid #dc3545",
    color: "#721c24"
  },
};

export default TutorAssignmentViewer;